import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { reportItem } from '../api/client';

interface ReportFormProps {
  kind: 'topic' | 'comment';
  id: string;
}

/**
 * Identical shape for a topic or a comment — a reason is the only input, and
 * the API side has no ownership check, so this is offered to everyone
 * signed in, not just other people's posts. See ModerationService/report in
 * the Phase 4 plan for what happens on submit: it hides immediately, same as
 * an automatic Block match.
 */
export function ReportForm({ kind, id }: ReportFormProps): React.JSX.Element {
  const [isReporting, setIsReporting] = useState(false);
  const [reason, setReason] = useState('');

  const report = useMutation({
    mutationFn: () => reportItem(kind, id, reason),
  });

  if (report.isSuccess) {
    return (
      <p className="hint" data-testid={`report-feedback-${kind}-${id}`}>
        Thanks — a moderator will review this.
      </p>
    );
  }

  if (!isReporting) {
    return (
      <button
        type="button"
        className="button secondary small"
        onClick={() => setIsReporting(true)}
        data-testid={`report-${kind}-${id}`}
      >
        Report
      </button>
    );
  }

  return (
    <form
      className="suspend-form"
      onSubmit={(event) => {
        event.preventDefault();
        report.mutate();
      }}
    >
      <label htmlFor={`report-reason-${kind}-${id}`}>Reason</label>
      <input
        id={`report-reason-${kind}-${id}`}
        value={reason}
        required
        onChange={(event) => setReason(event.target.value)}
      />
      <button
        type="submit"
        className="button small"
        disabled={report.isPending || reason.trim() === ''}
        data-testid={`confirm-report-${kind}-${id}`}
      >
        {report.isPending ? 'Reporting…' : 'Submit report'}
      </button>

      {report.error && (
        <p role="alert" className="notice warning">
          Could not submit that report. Try again.
        </p>
      )}
    </form>
  );
}
